import { LitElement, html, css, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/progress-ring/progress-ring.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import '../workflows/workflow-action-confirmation-dialog.js';
import '../workflows/workflow-timer-dialog.js';
import { getPercentElapsedForTimer } from '../../modules/katapult-workflows/WorkflowTimers.js';
import { OverviewActions } from './OverviewActions.js';

/** @import { Ref } from 'lit/directives/ref.js' */
/** @import { WorkflowActionConfirmationDialog } from '../workflows/workflow-action-confirmation-dialog.js' */
/** @import { WorkflowTimerDialog } from '../workflows/workflow-timer-dialog.js' */
/** @import { WorkflowActionsContext } from './OverviewActions.js' */

export class OverviewActionsButton extends LitElement {
  static properties = {
    jobId: { type: String },
    jobName: { type: String },
    jobModel: { type: String },
    jobMetadata: { type: Object },
    userGroup: { type: String },
    enabledFeatures: { type: Object },
    attachers: { type: Object },
    dropdownOpen: { type: Boolean, reflect: true, attribute: 'dropdown-open' },
    _loading: { type: Boolean, state: true },
    _loadError: { type: Boolean, state: true }
  };

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    sl-progress-ring {
      --size: 16px;
      --track-width: 2px;
    }
  `;

  constructor() {
    super();
    this.jobId = null;
    this.jobName = null;
    this.jobModel = null;
    this.jobMetadata = null;
    this.userGroup = null;
    this.enabledFeatures = null;
    this.attachers = null;
    // Used by pole-application-overview to set the z-index of the row when the dropdown is open
    this.dropdownOpen = false;
    this._loading = false;
    this._loadError = false;

    /** @type {OverviewActions} */
    this._actionsService = new OverviewActions();

    /** @type {Ref<import('@shoelace-style/shoelace').SlDropdown>} */
    this._dropdownRef = createRef();
    /** @type {Ref<WorkflowActionConfirmationDialog>} */
    this._confirmationDialogRef = createRef();
    /** @type {Ref<WorkflowTimerDialog>} */
    this._timerDialogRef = createRef();

    /**
     * Cache of job data
     * @type {Map<string, WorkflowActionsContext>}
     */
    this._contextByJobId = new Map();
  }

  render() {
    if (!this.jobMetadata?.app_type) return nothing;

    const context = this._contextByJobId.get(this.jobId);
    const actionButtons = context?.actionButtons ?? [];
    const timers = context?.timers ?? [];
    const hasContent = actionButtons.length > 0 || timers.length > 0;

    return html`
      <workflow-action-confirmation-dialog
        ${ref(this._confirmationDialogRef)}
        @action-completed=${this.#invalidateCache.bind(this)}
      ></workflow-action-confirmation-dialog>
      <workflow-timer-dialog ${ref(this._timerDialogRef)} @change=${this.#invalidateCache.bind(this)}></workflow-timer-dialog>

      <sl-dropdown
        ${ref(this._dropdownRef)}
        hoist
        @sl-show=${this.#handleDropdownShow.bind(this)}
        @sl-after-show=${this.#handleDropdownAfterShow.bind(this)}
        @sl-after-hide=${this.#handleDropdownAfterHide.bind(this)}
      >
        <katapult-button
          slot="trigger"
          iconOnly
          noBorder
          icon="bolt"
          loader-in-button
          .loading=${this._loading}
          title="Workflow actions"
        ></katapult-button>
        <sl-menu @sl-select=${this.#handleMenuSelect.bind(this)}>
          ${when(
            this._loadError,
            () => html`<sl-menu-item disabled>Failed to load actions</sl-menu-item>`,
            () =>
              when(
                hasContent,
                () => html`
                  ${when(
                    actionButtons.length > 0,
                    () => html`
                      <sl-menu-label>Actions</sl-menu-label>
                      ${map(
                        actionButtons,
                        (button, index) => html`
                          <sl-menu-item value=${`action:${index}`}>
                            ${when(button.icon, () => html`<katapult-icon slot="prefix" icon=${button.icon}></katapult-icon>`)}
                            ${button.label ?? button.$key}
                          </sl-menu-item>
                        `
                      )}
                    `
                  )}
                  ${when(actionButtons.length > 0 && timers.length > 0, () => html`<sl-divider></sl-divider>`)}
                  ${when(
                    timers.length > 0,
                    () => html`
                      <sl-menu-label>Timers</sl-menu-label>
                      ${map(
                        timers,
                        (timer, index) => html`
                          <sl-menu-item value=${`timer:${index}`}>
                            <sl-progress-ring slot="prefix" .value=${getPercentElapsedForTimer(timer)}></sl-progress-ring>
                            ${timer.label}
                          </sl-menu-item>
                        `
                      )}
                    `
                  )}
                `,
                () => html`<sl-menu-item disabled>No actions available</sl-menu-item>`
              )
          )}
        </sl-menu>
      </sl-dropdown>
    `;
  }

  /**
   * First uses cached data, or waits until data is loaded then display the dropdown
   * @param {CustomEvent} event
   */
  async #handleDropdownShow(event) {
    if (this._contextByJobId.has(this.jobId)) return;

    event.preventDefault();
    if (this._loading) return;

    await this.#loadContext();
    this._dropdownRef.value?.show();
  }

  #handleDropdownAfterShow() {
    this.dropdownOpen = true;
  }

  #handleDropdownAfterHide() {
    this.dropdownOpen = false;
  }

  async #loadContext() {
    const { jobId, jobModel, jobMetadata, userGroup } = this;
    if (!jobId || !jobModel || !jobMetadata?.app_type) return;

    this._loading = true;
    this._loadError = false;
    try {
      const context = await this._actionsService.getWorkflowActionsContext({
        jobId,
        jobModel,
        jobMetadata,
        userGroup,
        attachers: this.attachers
      });
      this._contextByJobId.set(jobId, context);
    } catch (error) {
      this._loadError = true;
      console.error('Failed to load workflow actions for job', jobId, error);
    } finally {
      this._loading = false;
    }
  }

  /** @param {CustomEvent} event */
  async #handleMenuSelect(event) {
    const value = event.detail?.item?.value;
    if (typeof value !== 'string') return;

    const context = this._contextByJobId.get(this.jobId);
    if (!context) return;

    const [type, indexString] = value.split(':');
    const index = Number(indexString);

    if (type === 'action') {
      const button = context.actionButtons?.[index];
      if (button) await this.#openActionDialog(button, context);
    } else if (type === 'timer') {
      const timer = context.timers?.[index];
      if (timer) this.#openTimerDialog(timer);
    }
  }

  /**
   * @param {Object} button
   * @param {WorkflowActionsContext} context
   */
  async #openActionDialog(button, context) {
    const dialog = this._confirmationDialogRef.value;
    if (!dialog) return;

    dialog.jobId = this.jobId;
    dialog.jobName = this.jobName;
    dialog.jobModel = this.jobModel;
    dialog.jobMetadata = this.jobMetadata;
    dialog.jobNodes = context.nodes;
    dialog.jobConnections = context.connections;
    dialog.currentStatus = context.currentStatus;
    dialog.companyAttributes = context.modelAttributes;
    dialog.attachers = context.attachers;
    dialog.appType = this.jobMetadata?.app_type;
    dialog.userGroup = this.userGroup;
    dialog.enabledFeatures = this.enabledFeatures ?? {};

    await dialog.open(button);
  }

  /** @param {import('../../modules/katapult-workflows/WorkflowTimers.js').Timer} timer */
  #openTimerDialog(timer) {
    const dialog = this._timerDialogRef.value;
    if (!dialog) return;
    dialog.timer = timer;
    dialog.open();
  }

  #invalidateCache() {
    this._contextByJobId.delete(this.jobId);
  }
}

customElements.define('overview-actions-button', OverviewActionsButton);
